import swal from "@sweetalert/with-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FiPlus, FiTrash, FiUser, FiBook } from "react-icons/fi";
import { useDispatch } from "react-redux";
import BigLoader from "../../components/BigLoader";
import Button from "../../components/Button";
import Card from "../../components/Card";
import Loader from "../../components/Loader";
import Modal from "../../components/Modal";
import TextInput from "../../components/TextInput";
import useDebounce from "../../debounce";
import { setInterface } from "../../redux/slices/interfaceSlice";
import service from "../../service";

export default function Mentor() {
  const dispatch = useDispatch();
  const [mentors, setMentors] = useState([]);
  const [courses, setCourses] = useState([]);
  const [getLoading, setGetLoading] = useState(true);
  const [postLoading, setPostLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [transferTo, setTransferTo] = useState("");
  const [assignCourseId, setAssignCourseId] = useState("");
  const [demoteTarget, setDemoteTarget] = useState(null);

  const _addModal = useRef();
  const _assignModal = useRef();
  const _demoteModal = useRef();
  const debounce = useDebounce((text) => searchUsers(text));

  const getMentors = () => {
    setGetLoading(true);
    service
      .get("/api/mentor")
      .then((res) => {
        setMentors(res.data || []);
        setGetLoading(false);
      })
      .catch(() => setGetLoading(false));
  };

  const getCourses = () => {
    service
      .get("/api/course", { params: { page: 1, limit: 100 } })
      .then((res) => setCourses(res.data?.data || []))
      .catch(() => setCourses([]));
  };

  const searchUsers = (q = "") => {
    setUserLoading(true);
    service
      .get("/api/user", { params: { q } })
      .then((res) => {
        setUsers(res.data || []);
        setUserLoading(false);
      })
      .catch(() => {
        setUsers([]);
        setUserLoading(false);
      });
  };

  const promoteUser = (userId) => {
    setPostLoading(true);
    service
      .post("/api/mentor", { user_id: userId })
      .then(() => {
        setPostLoading(false);
        _addModal.current.hide();
        getMentors();
        swal("Berhasil", "User dijadikan mentor", "success");
      })
      .catch((e) => {
        setPostLoading(false);
        swal("Gagal", e?.response?.data?.message || "Gagal menambah mentor", "error");
      });
  };

  const openDemote = (mentor) => {
    setDemoteTarget(mentor);
    setTransferTo("");
    if (mentor.course_count > 0) {
      _demoteModal.current.show();
    } else {
      confirmDemote(mentor, null);
    }
  };

  const confirmDemote = (mentor, transferUserId) => {
    const body = transferUserId ? { transfer_to: Number(transferUserId) } : {};
    setPostLoading(true);
    service
      .delete(`/api/mentor/${mentor.id}`, { data: body })
      .then((res) => {
        setPostLoading(false);
        _demoteModal.current?.hide();
        setDemoteTarget(null);
        getMentors();
        const moved = res.data?.transferred_courses || 0;
        swal(
          "Berhasil",
          moved
            ? `Mentor dihapus. ${moved} course dipindah ke mentor pengganti.`
            : "Mentor dihapus.",
          "success"
        );
      })
      .catch((e) => {
        setPostLoading(false);
        const msg = e?.response?.data?.message || "Gagal menghapus mentor";
        if (e?.response?.status === 400 && e?.response?.data?.course_count) {
          setDemoteTarget(mentor);
          _demoteModal.current.show();
          swal("Perhatian", msg, "warning");
        } else {
          swal("Gagal", msg, "error");
        }
      });
  };

  const openAssign = (mentor) => {
    setSelectedMentor(mentor);
    setAssignCourseId("");
    _assignModal.current.show();
  };

  const assignCourse = () => {
    if (!selectedMentor || !assignCourseId) return;
    setPostLoading(true);
    service
      .post(`/api/mentor/${selectedMentor.id}/courses`, {
        course_id: Number(assignCourseId),
      })
      .then(() => {
        setPostLoading(false);
        _assignModal.current.hide();
        getMentors();
        swal("Berhasil", "Course di-assign ke mentor", "success");
      })
      .catch((e) => {
        setPostLoading(false);
        swal("Gagal", e?.response?.data?.message || "Gagal assign", "error");
      });
  };

  const unassignCourse = (mentor, course) => {
    swal({
      title: "Lepas course?",
      text: `Hapus ${mentor.fullname} sebagai mentor di "${course.title}"?`,
      icon: "warning",
      buttons: ["Batal", true],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      setPostLoading(true);
      service
        .delete(`/api/mentor/${mentor.id}/courses/${course.id}`)
        .then(() => {
          setPostLoading(false);
          getMentors();
          swal("Berhasil", "Mentor dilepas dari course", "success");
        })
        .catch((e) => {
          setPostLoading(false);
          swal("Gagal", e?.response?.data?.message || "Gagal", "error");
        });
    });
  };

  useEffect(() => {
    dispatch(
      setInterface({
        active: "Settings",
        pageTitle: "Mentor Online Course",
        rightButton: null,
      })
    );
    getMentors();
    getCourses();
  }, [dispatch]);

  const otherMentors = mentors.filter((m) => m.id !== demoteTarget?.id);

  return (
    <Fragment>
      <Helmet>
        <title>Mentor Online Course</title>
      </Helmet>
      <Card>
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-black-200">
            Kelola mentor: assign ke Online Course, atau hapus (dengan pindah course).
          </p>
          <Button
            type="button"
            color="green"
            className="flex items-center"
            onClick={() => {
              setUsers([]);
              _addModal.current.show();
            }}
          >
            <FiPlus className="mr-1" /> Tambah Mentor
          </Button>
        </div>

        {getLoading ? (
          <div className="h-64 flex justify-center items-center flex-col">
            <Loader className="bg-black-100" />
            <span className="mt-3 text-black-300 text-sm">Memuat</span>
          </div>
        ) : mentors.length ? (
          mentors.map((m) => (
            <div
              key={m.id}
              className="border border-white-200 rounded-sm p-3 lg:p-4 mt-3 transition-colors hover:bg-white-200"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
                  <FiUser size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-black-200">{m.fullname}</h3>
                  <p className="text-sm text-black-300">
                    @{m.username} · {m.email}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(m.courses || []).length ? (
                      m.courses.map((c) => (
                        <span
                          key={c.id}
                          className="text-xs bg-white-200 px-2 py-1 rounded flex items-center gap-1 text-black-300"
                        >
                          <FiBook /> {c.title}
                          <button
                            type="button"
                            className="ml-1 text-red-200 hover:text-red-300"
                            title="Lepas dari course"
                            onClick={() => unassignCourse(m, c)}
                          >
                            ×
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-black-300">
                        Belum assign course
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    color="white"
                    noFixedHeight
                    className="text-sm h-9"
                    onClick={() => openAssign(m)}
                  >
                    Assign Course
                  </Button>
                  <Button
                    type="button"
                    color="red"
                    noFixedHeight
                    className="text-sm h-9 flex items-center justify-center"
                    onClick={() => openDemote(m)}
                  >
                    <FiTrash className="mr-1" /> Hapus
                  </Button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center h-40 flex items-center justify-center text-black-300">
            Belum ada mentor. Klik Tambah Mentor.
          </div>
        )}
      </Card>

      <Modal title="Tambah Mentor" ref={_addModal}>
        <TextInput
          containerClassName="mb-4"
          placeholder="Cari user (nama / username)"
          onChange={(e) => {
            setUserLoading(true);
            debounce(e.target.value);
          }}
        />
        {userLoading ? (
          <div className="flex justify-center h-24">
            <Loader className="bg-black-200" />
          </div>
        ) : (
          users.map((u) => (
            <button
              key={u.id}
              type="button"
              className="w-full text-left py-3 border-b border-white-200 last:border-b-0 hover:bg-white-200 transition-colors flex items-center rounded-sm px-2"
              onClick={() => promoteUser(u.id)}
            >
              <span className="w-10 h-10 rounded-full bg-white-200 flex items-center justify-center mr-3 text-black-300">
                <FiUser size={18} />
              </span>
              <div>
                <span className="font-bold text-black-200">{u.fullname}</span>
                <br />
                <span className="text-sm text-black-300">@{u.username}</span>
              </div>
            </button>
          ))
        )}
      </Modal>

      <Modal title="Assign Online Course" ref={_assignModal}>
        <p className="mb-3 text-sm">
          Mentor: <strong>{selectedMentor?.fullname}</strong>
        </p>
        <label className="text-black-200 font-semibold text-sm mb-1 block">
          Pilih course
        </label>
        <select
          className="w-full h-11 border border-white-300 rounded-sm px-3 bg-white-100 mb-4 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
          value={assignCourseId}
          onChange={(e) => setAssignCourseId(e.target.value)}
        >
          <option value="">— pilih —</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
        <Button type="button" color="blue" className="w-full" onClick={assignCourse}>
          Simpan
        </Button>
      </Modal>

      <Modal title="Hapus Mentor" ref={_demoteModal}>
        <p className="mb-3">
          <strong>{demoteTarget?.fullname}</strong> masih mentor di{" "}
          <strong>{demoteTarget?.course_count || 0}</strong> online course.
        </p>
        <p className="mb-3 text-sm text-black-200">
          Pilih mentor pengganti. Semua course akan dipindah ke mentor tersebut.
        </p>
        <label className="text-black-200 font-semibold text-sm mb-1 block">
          Transfer ke mentor
        </label>
        <select
          className="w-full h-11 border border-white-300 rounded-sm px-3 bg-white-100 mb-4 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
          value={transferTo}
          onChange={(e) => setTransferTo(e.target.value)}
        >
          <option value="">— pilih mentor pengganti —</option>
          {otherMentors.map((m) => (
            <option key={m.id} value={m.id}>
              {m.fullname} (@{m.username})
            </option>
          ))}
        </select>
        {!otherMentors.length ? (
          <p className="text-sm text-red-200 mb-3">
            Tidak ada mentor lain. Tambah mentor baru dulu, lalu hapus yang ini.
          </p>
        ) : null}
        <div className="flex gap-2">
          <Button
            type="button"
            color="white"
            className="flex-1"
            onClick={() => _demoteModal.current.hide()}
          >
            Batal
          </Button>
          <Button
            type="button"
            color="red"
            className="flex-1"
            disabled={!transferTo}
            onClick={() => demoteTarget && confirmDemote(demoteTarget, transferTo)}
          >
            Hapus & Pindahkan
          </Button>
        </div>
      </Modal>

      {postLoading ? <BigLoader /> : null}
    </Fragment>
  );
}
